import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ✅ Imports des logos depuis assets
import logoProjectBoost from "../assets/images/logo-projectboost.png";
import logoBelan from "../assets/images/logo-belan.jpg";

function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'fr');

  const toggleLanguage = () => {
    const newLang = currentLang === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header avec changement de langue */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src={logoProjectBoost}
              alt="ProjectBoost" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-2xl font-bold">
              <span className="text-green-600">PROJECT</span>
              <span className="text-blue-600">BOOST</span>
            </span>
          </div>
          
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition"
          >
            <span className="text-2xl">{currentLang === 'fr' ? '🇫🇷' : '🇬🇧'}</span>
            <span className="font-medium text-gray-700">
              {currentLang === 'fr' ? 'Français' : 'English'}
            </span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          {/* Logo principal */}
          <div className="mb-8 flex justify-center">
            <img 
              src={logoProjectBoost}
              alt="ProjectBoost Logo" 
              className="h-32 w-32 object-contain animate-pulse"
            />
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-green-600">{t('landingHero1')}</span>
            <br />
            <span className="text-blue-600">{t('landingHero2')}</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto">
            {t('landingSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition"
            >
              🚀 {t('landingGetStarted')}
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg shadow hover:shadow-lg transition border-2 border-blue-600"
            >
              {t('landingSignIn')}
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            {t('landingWhyTitle')}
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            {t('landingWhySubtitle')}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center p-6 rounded-xl hover:shadow-xl transition bg-gradient-to-br from-green-50 to-white">
              <div className="text-6xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">
                {t('landingFeature1Title')}
              </h3>
              <p className="text-gray-600">
                {t('landingFeature1Description')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center p-6 rounded-xl hover:shadow-xl transition bg-gradient-to-br from-blue-50 to-white">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">
                {t('landingFeature2Title')}
              </h3>
              <p className="text-gray-600">
                {t('landingFeature2Description')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center p-6 rounded-xl hover:shadow-xl transition bg-gradient-to-br from-green-50 to-white">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">
                {t('landingFeature3Title')}
              </h3>
              <p className="text-gray-600">
                {t('landingFeature3Description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
            {t('landingHowTitle')}
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h4 className="font-bold text-lg mb-2">{t('landingStep1')}</h4>
              <p className="text-gray-600 text-sm">{t('landingStep1Description')}</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h4 className="font-bold text-lg mb-2">{t('landingStep2')}</h4>
              <p className="text-gray-600 text-sm">{t('landingStep2Description')}</p>
            </div>

            <div className="text-center">
              <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h4 className="font-bold text-lg mb-2">{t('landingStep3')}</h4>
              <p className="text-gray-600 text-sm">{t('landingStep3Description')}</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h4 className="font-bold text-lg mb-2">{t('landingStep4')}</h4>
              <p className="text-gray-600 text-sm">{t('landingStep4Description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('landingCtaTitle')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('landingCtaSubtitle')}
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-white text-blue-600 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition"
          >
            ✨ {t('landingCtaButton')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Logo BEL-AN Prod */}
            <div className="flex items-center gap-4">
              <img 
                src={logoBelan}
                alt="BEL-AN Prod" 
                className="h-16 w-16 object-contain rounded-full bg-white p-2"
              />
              <div>
                <p className="text-sm text-gray-400">{t('landingPoweredBy')}</p>
                <p className="font-bold text-lg">BEL-AN Prod</p>
              </div>
            </div>

            {/* Logo ProjectBoost */}
            <div className="flex items-center gap-3">
              <img 
                src={logoProjectBoost}
                alt="ProjectBoost" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <p className="text-2xl font-bold">
                  <span className="text-green-400">PROJECT</span>
                  <span className="text-blue-400">BOOST</span>
                </p>
                <p className="text-xs text-gray-400">
                  © 2026 {t('landingAllRights')}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-sm text-gray-400">
            <p className="mb-4">{t('landingFooterText')}</p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="/mentions-legales" className="hover:text-gray-200 transition">
                {currentLang === 'fr' ? 'Mentions légales' : 'Legal Notice'}
              </a>
              <a href="/confidentialite" className="hover:text-gray-200 transition">
                {currentLang === 'fr' ? 'Confidentialité' : 'Privacy Policy'}
              </a>
              <a href="/cgu" className="hover:text-gray-200 transition">
                {currentLang === 'fr' ? 'CGU' : 'Terms of Use'}
              </a>
              <a href="/cgv" className="hover:text-gray-200 transition">
                {currentLang === 'fr' ? 'CGV' : 'Terms of Sale'}
              </a>
              <a href="/cookies" className="hover:text-gray-200 transition">
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
